import { Body, PopoverContent } from '@e2n/ui/v2';
import { faCalendarRange, faUser } from '@fortawesome/pro-regular-svg-icons';
import {
  faCircle,
  faCircleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { KalenderByMitarbeiterStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByMitarbeiter/KalenderStation';
import { StyleProps } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/types';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { RowDivider } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/RowDivider';

export function MitarbeiterStationIntervalContent({
  interval,
  konfliktStationen,
  onMouseEnter,
  onMouseLeave,
}: {
  interval: KalenderByMitarbeiterStation;
  konfliktStationen: ArbeitsbereichStation[];
  onMouseEnter: (event: React.MouseEvent) => void;
  onMouseLeave: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className="w-full h-full bg-(--bg-color) border-(--border-color) border border-solid rounded-lg flex items-center justify-start px-3"
        style={
          {
            '--bg-color': interval.arbeitsbereich.getBorderColor(),
            '--border-color': interval.arbeitsbereich.getBorderColor(),
            '--text-color': interval.arbeitsbereich.getTextColor(),
          } as StyleProps
        }>
        <Body className="truncate text-(--text-color)" size="sm">
          {konfliktStationen.length > 0 && (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              // weißer Hintergrund, damit das rote Icon auf jeder Markierungsfarbe lesbar bleibt
              className="mr-1 size-3.5 rounded-full bg-backgrounds-primary ring-backgrounds-primary text-error-red-200"
            />
          )}
          {interval.arbeitsbereich.name}
        </Body>
      </div>
      <PopoverContent className="flex flex-col gap-3 p-4 text-sm text-labels-primary bg-backgrounds-primary border-neutrals-v2-200 rounded-xl w-88">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faCircle}
                color={interval.arbeitsbereich.getBorderColor()}
                className="size-2.5"
              />
              <Body weight="semibold">{interval.arbeitsbereich.name}</Body>
            </div>
            <Location
              className="text-labels-secondary"
              standort={interval.arbeitsbereich.standort}
            />
          </div>
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon className="size-4" icon={faCalendarRange} />
              {t('domain.mitarbeiter.rotationsplan.zeitraum')}
            </div>
            <Body>
              {dayjs(interval.station.von).format('DD.MM.YYYY') +
                ' – ' +
                dayjs(interval.station.bis).format('DD.MM.YYYY')}
            </Body>
          </div>
          {interval.station.abteilungsleiter && (
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon className="size-4" icon={faUser} />
                {t('domain.mitarbeiter.rotationsplan.abteilungsleitung')}
              </div>
              <Body>
                {interval.station.abteilungsleiter.vorname +
                  ' ' +
                  interval.station.abteilungsleiter.nachname}
              </Body>
            </div>
          )}
        </div>
        {konfliktStationen.length > 0 && (
          <>
            <RowDivider />
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1 font-semibold">
                <FontAwesomeIcon
                  className="size-4 text-error-red-200"
                  icon={faCircleExclamation}
                />
                <Body className="ml-1" weight="semibold">
                  {t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.konflikt.count',
                    { count: konfliktStationen.length },
                  )}
                </Body>
              </div>
              {konfliktStationen.map((station) => (
                <div
                  key={station.id}
                  className="flex items-center justify-between gap-6">
                  <div className="flex items-center min-w-0 gap-2">
                    <FontAwesomeIcon
                      icon={faCircle}
                      color={station.arbeitsbereich.getBorderColor()}
                      size="xs"
                    />
                    <Body className="truncate">
                      {station.arbeitsbereich.name}
                    </Body>
                  </div>
                  <div className="whitespace-nowrap">
                    {dayjs(station.von).format('DD.MM') +
                      ' - ' +
                      dayjs(station.bis).format('DD.MM.YYYY')}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </PopoverContent>
    </>
  );
}
